/**
 * Days a worker is owed without working them.
 *
 * Two entitlements live here, and both were missing entirely. Both turn on the
 * same question — which days would this person ordinarily have worked — which
 * is why they share a file.
 *
 * SOUTH AFRICA ONLY. BCEA sections 18 and 20. See the future-markets note:
 * when Shifty sells elsewhere this goes behind CountryPack rather than being
 * edited in place.
 */

import { fromISODate, toISODate } from "./calendar";
import { holidaysFor, type PublicHoliday } from "./holidays";
import type { EmploymentTerms } from "./types";

/** Which weekdays somebody works, 0 = Sunday to match Date.getDay(). */
export type WorksWeekday = (weekday: number) => boolean;

/** Every calendar date from `from` to `to`, inclusive. */
function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const end = fromISODate(to);
  let d = fromISODate(from);
  if (!d || !end) return out;
  while (d <= end) {
    out.push(toISODate(d));
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  return out;
}

function calendarFor(dates: string[], declared?: PublicHoliday[]) {
  // Undefined, not [], so holidaysFor falls back to the declared days the
  // client loaded. An empty array would silently mean "no proclamations".
  return holidaysFor(dates.map((d) => Number(d.slice(0, 4))), declared);
}

export interface LeaveDays {
  /** Days away from first to last, inclusive — what the worker asked for. */
  calendar: number;
  /** Days actually taken off the annual entitlement. */
  used: number;
  /** Days inside the period they would not have worked anyway. */
  rest: number;
  /** Public holidays inside the period on a day they would have worked. Each
   *  one is a day the entitlement must not be charged for — s20(8). */
  holidays: PublicHoliday[];
}

/**
 * What a leave request actually costs the annual entitlement.
 *
 * Shifty counted calendar days. The entitlement is in working days —
 * CountryPack says so in as many words, "Working days per annual leave cycle",
 * and South Africa's 15 is the standard rendering of the BCEA's "21
 * consecutive days" for somebody working five days a week.
 *
 * Counting one against the other is not a rounding error. A worker taking the
 * three weeks the Act grants them, Monday the 1st to Sunday the 21st, had 21
 * days taken off a 15-day balance: their whole year's leave, overdrawn by six,
 * for exactly the leave they are entitled to.
 *
 * And then s20(8):
 *
 *   An employer must grant an employee an additional day of paid leave if a
 *   public holiday falls on a day during an employee's annual leave on which
 *   the employee would ordinarily have worked.
 *
 * The Act grants an extra day rather than declining to charge for one. The
 * balance ends up the same either way, and not charging is the simpler thing
 * to hold in one number — but it is worth knowing the statute says it the
 * other way round, because a payslip or a dispute will use the statute's words.
 */
export function leaveDaysUsed(
  from: string,
  to: string,
  opts: { worksWeekday?: WorksWeekday; declared?: PublicHoliday[] } = {},
): LeaveDays {
  const dates = datesBetween(from, to);
  const holidays = calendarFor(dates, opts.declared);
  const out: LeaveDays = { calendar: dates.length, used: 0, rest: 0, holidays: [] };

  for (const iso of dates) {
    const weekday = fromISODate(iso)!.getDay();
    // No roster recorded means we do not know, and guessing "does not work"
    // would quietly hand back leave the worker never took. Assume they work it.
    const works = opts.worksWeekday ? opts.worksWeekday(weekday) : true;
    if (!works) { out.rest += 1; continue; }

    const holiday = holidays.get(iso);
    if (holiday) { out.holidays.push(holiday); continue; }

    out.used += 1;
  }
  return out;
}

export interface UnworkedHoliday {
  holiday: PublicHoliday;
  /** A day's wage, s18(2)(a): "at least the wage that the employee would
   *  ordinarily have received for work on that day". */
  amount: number;
}

/**
 * Public holidays in a period that were paid but not worked — s18(2)(a).
 *
 *   If a public holiday falls on a day on which an employee would ordinarily
 *   work, an employer must pay an employee who does not work on the public
 *   holiday, at least the wage that the employee would ordinarily have
 *   received for work on that day.
 *
 * Shifty knows only about shifts, so this entitlement was simply absent: no
 * shift, no pay. A worker who stayed home on Heritage Day as the Act intends
 * was paid nothing for it.
 *
 * Days already covered by approved leave are excluded. The worker is paid for
 * those as leave, and s20(8) hands back the leave day rather than paying twice.
 */
export function unworkedHolidays(opts: {
  from: string;
  to: string;
  terms: EmploymentTerms;
  worksWeekday?: WorksWeekday;
  /** Whether a shift was actually worked on that date. */
  workedOn: (iso: string) => boolean;
  /** Whether approved leave covers that date. */
  onLeave?: (iso: string) => boolean;
  declared?: PublicHoliday[];
  /**
   * The day the question is being asked on. Holidays after it are left out.
   *
   * Without it, a payslip for the month somebody is standing in pays for a
   * public holiday that has not arrived — and cannot, because whether they
   * work it is exactly what is not yet known. Found on a phone: a September
   * payslip run on the 22nd was quietly R288 heavy for Heritage Day on the
   * 24th. Optional, because a screen that is looking ahead on purpose — a
   * roster, next week's register — is entitled to see the whole period.
   */
  asAt?: string;
}): UnworkedHoliday[] {
  const dates = datesBetween(opts.from, opts.to);
  const holidays = calendarFor(dates, opts.declared);
  const dayWage = opts.terms.rate * opts.terms.ordinaryDaily;

  const out: UnworkedHoliday[] = [];
  for (const iso of dates) {
    const holiday = holidays.get(iso);
    if (!holiday) continue;

    if (opts.asAt && iso > opts.asAt) continue;                    // has not happened yet
    const weekday = fromISODate(iso)!.getDay();
    if (opts.worksWeekday && !opts.worksWeekday(weekday)) continue; // s18(2) only
    if (opts.workedOn(iso)) continue;                              // paid as a worked day
    if (opts.onLeave?.(iso)) continue;                             // paid as leave, s20(8)

    out.push({ holiday, amount: dayWage });
  }
  return out;
}

/* ---------- annual leave taken ---------- */

export type LeaveType = "annual" | "sick" | "family";
export type LeaveStatus = "pending" | "approved" | "declined";

export interface LeaveRequest {
  id: string;
  type: LeaveType;
  startsOn: string;
  endsOn: string;
  /** What the rules worked out this costs. Never rewritten. */
  days: number;
  reason: string;
  status: LeaveStatus;
  createdAt: string;
  /**
   * What the employer actually charged, when they disagreed with `days`.
   *
   * Undefined means they accepted the computed figure. Both are kept so the
   * register can always answer "what did Shifty say, and what did you do" —
   * an override that erased the original would be a quieter record, not a
   * truer one. Zero is a real value: a day forgiven is a decision.
   */
  daysCharged?: number;
  /** Why the charge differs. Shown to the worker. */
  daysNote?: string;
}

/** What this request actually costs the entitlement. */
export const daysCharged = (r: Pick<LeaveRequest, "days" | "daysCharged">): number =>
  r.daysCharged ?? r.days;

/**
 * Days already spent out of the annual entitlement.
 *
 * Only approved days count against it. A pending request has not been granted,
 * and showing it as spent would tell somebody they have less leave than they
 * do — which is the direction of error that costs a person a holiday.
 *
 * In core rather than in the app's feature layer, because the dashboard shows
 * the same balance and a second implementation of it would eventually disagree
 * with the first over somebody's holiday.
 */
export function annualTaken(
  requests: Pick<LeaveRequest, "type" | "status" | "days" | "daysCharged">[],
): number {
  return requests
    .filter((r) => r.type === "annual" && r.status === "approved")
    .reduce((sum, r) => sum + daysCharged(r), 0);
}

/**
 * What the Act would say about charging a different number of days.
 *
 * Warnings, never refusals — the same bargain as pay adjustments. An employer
 * has reasons Shifty cannot see, and a register that declines to record what
 * happened is tidy and false. Both directions are worth a word, and only one
 * of them is in the worker's favour.
 */
export function leaveChargeWarnings(opts: {
  /** What the rules computed. */
  computed: number;
  /** What the employer is charging. */
  charged: number;
  /** Annual days left before this request, when it is annual leave. */
  remaining?: number;
  type: LeaveType;
  note?: string;
}): { text: string; section: string }[] {
  const out: { text: string; section: string }[] = [];
  if (opts.charged === opts.computed) return out;

  if (opts.charged > opts.computed) {
    out.push({
      section: "BCEA s20(2)",
      text:
        `Shifty counted ${opts.computed} working ${opts.computed === 1 ? "day" : "days"} in ` +
        `these dates — weekends and public holidays are not charged. Taking ${opts.charged} ` +
        "charges them for time they were not away.",
    });
  }

  if (!opts.note?.trim()) {
    out.push({
      section: "Say why",
      text:
        "A charge that differs from the dates will be read by the worker. Without a reason " +
        "beside it, it looks like an error rather than a decision.",
    });
  }

  if (opts.type === "annual" && opts.remaining != null && opts.charged > opts.remaining) {
    // Somebody already overdrawn needs a different sentence. "More than the -1
    // days left" is arithmetic read aloud, not English, and the reader has to
    // work out what it means at the moment they are deciding something.
    out.push(opts.remaining <= 0
      ? {
          section: "Check this",
          text:
            opts.remaining === 0
              ? "Their annual entitlement is already used up, so all of this goes over."
              : `They are already ${-opts.remaining} ${-opts.remaining === 1 ? "day" : "days"} ` +
                "over their annual entitlement, and this adds to it.",
        }
      : {
          section: "Check this",
          text:
            `That is more than the ${opts.remaining} ${opts.remaining === 1 ? "day" : "days"} left ` +
            "in their annual entitlement, so the balance goes negative.",
        });
  }

  return out;
}
